import { ButtonProps, Grid } from '@chakra-ui/react';
import { DPMonth, useDatePicker } from '@rehookify/datepicker';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MButton from '../MButton';
import { MBox } from '../chakra';

interface MonthViewProps {
  months: DPMonth[];
  interval?: 1 | 3 | 6;
  anchorMonth?: number;
  onChange: (month: DPMonth) => void;
  monthBtnPropsFn: ReturnType<
    typeof useDatePicker
  >['propGetters']['monthButton'];
  onNavigateYear?: (direction: 'prev' | 'next') => void;
}

/**
 * Displays the months in a grid and allows user to select a month
 */
export const MonthView = ({
  months,
  interval = 1,
  anchorMonth = 1,
  onChange,
  monthBtnPropsFn,
  onNavigateYear,
}: MonthViewProps) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null,
  );

  const monthsWithDisabled = useMemo(
    () =>
      months.map((month, i) => {
        const relativeIndex = (i - anchorMonth + 1 + 12) % 12;
        const isDisabled =
          relativeIndex % interval !== 0 || !!monthBtnPropsFn(month).disabled;
        return { month, isDisabled };
      }),
    [months, interval, anchorMonth, monthBtnPropsFn],
  );

  const rovingIndex = useMemo(() => {
    const selectedIdx = monthsWithDisabled.findIndex(
      ({ month }) => month.selected,
    );
    if (selectedIdx >= 0 && !monthsWithDisabled[selectedIdx].isDisabled) {
      return selectedIdx;
    }
    const activeIdx = monthsWithDisabled.findIndex(({ month }) => month.now);
    if (activeIdx >= 0 && !monthsWithDisabled[activeIdx].isDisabled) {
      return activeIdx;
    }
    const firstEnabledIdx = monthsWithDisabled.findIndex(
      ({ isDisabled }) => !isDisabled,
    );
    return firstEnabledIdx >= 0 ? firstEnabledIdx : 0;
  }, [monthsWithDisabled]);

  useEffect(() => {
    if (pendingFocusIndex !== null) {
      // After a year change, the target month may be disabled by min/max
      // constraints. Scan outward (forward, then backward) to the nearest
      // enabled month so keyboard focus is preserved.
      let focusIndex: number | null = null;
      if (!monthsWithDisabled[pendingFocusIndex]?.isDisabled) {
        focusIndex = pendingFocusIndex;
      } else {
        for (let i = pendingFocusIndex + 1; i < 12; i += 1) {
          if (!monthsWithDisabled[i].isDisabled) {
            focusIndex = i;
            break;
          }
        }
        if (focusIndex === null) {
          for (let i = pendingFocusIndex - 1; i >= 0; i -= 1) {
            if (!monthsWithDisabled[i].isDisabled) {
              focusIndex = i;
              break;
            }
          }
        }
      }
      if (focusIndex !== null) {
        buttonRefs.current[focusIndex]?.focus();
      }
      setPendingFocusIndex(null);
    }
  }, [pendingFocusIndex, months, monthsWithDisabled]);

  const findNextEnabledIndex = useCallback(
    (startIndex: number, step: number): number | null => {
      let index = startIndex;
      while (index >= 0 && index < 12) {
        if (!monthsWithDisabled[index].isDisabled) {
          return index;
        }
        index += step;
      }
      return null;
    },
    [monthsWithDisabled],
  );

  const handleGridKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      let nextIndex: number | null = null;
      let step = 0;

      switch (event.key) {
        case 'ArrowRight':
          step = 1;
          nextIndex = currentIndex + 1;
          break;
        case 'ArrowLeft':
          step = -1;
          nextIndex = currentIndex - 1;
          break;
        case 'ArrowDown':
          step = 3;
          nextIndex = currentIndex + 3;
          break;
        case 'ArrowUp':
          step = -3;
          nextIndex = currentIndex - 3;
          break;
        default:
          return;
      }

      event.preventDefault();

      if (nextIndex < 0) {
        if (onNavigateYear) {
          onNavigateYear('prev');
          setPendingFocusIndex(12 + nextIndex);
        }
        return;
      }

      if (nextIndex >= 12) {
        if (onNavigateYear) {
          onNavigateYear('next');
          setPendingFocusIndex(nextIndex - 12);
        }
        return;
      }

      const enabledIndex = findNextEnabledIndex(nextIndex, step);
      if (enabledIndex !== null) {
        buttonRefs.current[enabledIndex]?.focus();
      }
    },
    [findNextEnabledIndex, onNavigateYear],
  );

  return (
    <Grid
      gridTemplateColumns="repeat(3, minmax(0, 1fr))"
      rowGap="0.5rem"
      columnGap=".1rem"
      overflowY="auto"
    >
      {monthsWithDisabled.map(({ month, isDisabled }, index) => (
        <MonthButton
          key={month.month}
          ref={(el) => {
            buttonRefs.current[index] = el;
          }}
          calendarMonth={{
            ...month,
            disabled: isDisabled,
          }}
          {...monthBtnPropsFn(month)}
          disabled={isDisabled}
          tabIndex={index === rovingIndex ? 0 : -1}
          onClick={() => !isDisabled && onChange(month)}
          onGridKeyDown={(event) => handleGridKeyDown(event, index)}
        />
      ))}
    </Grid>
  );
};

interface MonthButtonProps extends ButtonProps {
  calendarMonth: DPMonth;
  onGridKeyDown?: (event: React.KeyboardEvent) => void;
}

const MonthButton = forwardRef<HTMLButtonElement, MonthButtonProps>(
  ({ calendarMonth, onGridKeyDown, ...rest }, ref) => {
    const { month, active, disabled, selected } = calendarMonth;
    let buttonProps: ButtonProps = {};
    // On the span, not the button: see the note in DatePickerCalendarView.
    let textColor = disabled ? 'fg.muted' : 'fg.primary';

    if (selected) {
      textColor = 'fg.onAccent';
      buttonProps = {
        ...buttonProps,
        bg: 'tIndigo.base',
        _hover: {
          bg: 'tIndigo.base',
        },
        _focus: {
          bg: 'tIndigo.base',
        },
      };
    }

    if (active) {
      buttonProps = { ...buttonProps, border: '1px solid tGray.lightPurple' };
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        onGridKeyDown?.(event);
      }
    };

    return (
      <MButton
        ref={ref}
        variant="tertiary"
        minW="unset"
        h="32px"
        m={1}
        isDisabled={disabled}
        fontWeight={400}
        borderRadius="3"
        _focus={{
          bg: 'inherit',
        }}
        _focusVisible={{
          boxShadow: 'focus',
        }}
        onKeyDown={handleKeyDown}
        {...buttonProps}
        {...rest}
      >
        <MBox as="span" color={textColor}>
          {month}
        </MBox>
      </MButton>
    );
  },
);
